<template>
  <video
      ref="videoEl"
      playsinline
      :autoplay="autoplay"
      :loop="loop"
      :muted="muted"
      preload="auto"
      :class="props.videoClass"
      :poster="currentPoster || undefined"
  />
</template>

<script setup>
import {onBeforeUnmount, onMounted, ref, watch} from 'vue';

const props = defineProps({
  src: {type: String, required: true},
  srcMobile: {type: String, default: ''},
  poster: {type: String, default: ''},
  posterMobile: {type: String, default: ''},
  mobileBreakpoint: {type: Number, default: 768},
  videoClass: {type: [String, Array, Object], default: ''},
  unloadOnExit: {type: Boolean, default: false},
  autoplay: {type: Boolean, default: true},
  loop: {type: Boolean, default: true},
  muted: {type: Boolean, default: true}
});

// Refs
const videoEl = ref(null);
const hasLoaded = ref(false);
const currentSrc = ref(null);
const currentPoster = ref(null);
const observer = ref(null);
const id = ref('unknown'); // identifier (filename) for logs
const isInView = ref(false);

function extractFileName(path) {
  if (!path) return 'unknown';
  return path.split('/').pop();
}

function selectSourcesAndLog(context = 'init') {
  // Decide which src/poster to use (mobile vs. desktop)
  if (typeof window !== 'undefined') {
    const isMobile = window.matchMedia
        ? window.matchMedia(`(max-width: ${props.mobileBreakpoint}px)`).matches
        : window.innerWidth <= props.mobileBreakpoint;

    currentSrc.value = isMobile && props.srcMobile ? props.srcMobile : props.src;
    currentPoster.value = isMobile && props.posterMobile ? props.posterMobile : props.poster;
    id.value = extractFileName(currentSrc.value);

  } else {
    // Fallback (shouldn’t really hit in the client-only lifecycle)
    currentSrc.value = props.src;
    currentPoster.value = props.poster;
    id.value = extractFileName(currentSrc.value);
  }
}

onMounted(() => {
  const el = videoEl.value;
  if (!el) {
    return;
  }

  selectSourcesAndLog('init');

  // IntersectionObserver: lazy load plus unload
  observer.value = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          isInView.value = entry.isIntersecting;
          if (entry.isIntersecting) {

            if (!hasLoaded.value && currentSrc.value) {
              el.src = currentSrc.value;
              hasLoaded.value = true;
              if (props.autoplay) {
                el.play().catch(() => {
                });
              }
            }
            if (props.autoplay) {
              el.play().catch(() => {
              });
            }
          } else {

            el.pause();
            if (props.unloadOnExit && hasLoaded.value) {
              el.removeAttribute('src');
              el.load();
              hasLoaded.value = false;
            }
          }
        });
      },
      {threshold: 0.5}
  );

  observer.value.observe(el);
});

onBeforeUnmount(() => {
  if (observer.value) {
    observer.value.disconnect();
  }

  const el = videoEl.value;
  if (el) {
    el.pause();
  }
});

// Watch for source (and poster) changes and update the video element accordingly
watch(
    () => [props.src, props.srcMobile, props.poster, props.posterMobile],
    () => {
      const el = videoEl.value;
      if (!el) return;

      // Re-select sources (mobile vs desktop) and log
      selectSourcesAndLog('props-change');

      // If currently in view, swap the source immediately and play
      if (isInView.value && currentSrc.value) {
        el.src = currentSrc.value;
        el.load();
        hasLoaded.value = true;
        if (props.autoplay) {
          el.play().catch(() => {
          });
        }
      } else {
        el.removeAttribute('src');
        el.load();
        hasLoaded.value = false;
      }
    }
);
</script>